import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Flame, Sparkles, Megaphone } from "lucide-react";
import { api, IMAGES } from "../lib/api";
import { useSite } from "../context/SiteContext";
import { isExternal } from "../lib/icons";
import { ProductGrid } from "../components/ProductCard";
import { Spinner, btnYellow } from "../components/ui";

const THEMES = {
  green: { bg: "bg-bo-green", grad: "from-bo-green via-bo-green/85 to-transparent", fallback: IMAGES.merchant, cta: btnYellow },
  dark: { bg: "bg-bo-ink", grad: "from-black/75 via-black/40 to-transparent", fallback: IMAGES.rider, cta: "inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-bold text-bo-ink hover:bg-bo-yellow" },
  yellow: { bg: "bg-bo-yellow", grad: "from-bo-yellow via-bo-yellow/80 to-transparent", fallback: IMAGES.panorama, cta: "inline-flex items-center gap-2 rounded-full bg-bo-ink px-5 py-2.5 text-sm font-bold text-white" },
};
const LEGACY_CTA = { bomarket: "banner-shop-now-btn", obogo: "banner-learn-more-btn" };

export const SmartLink = ({ to, children, ...rest }) => (isExternal(to)
  ? <a href={to} target="_blank" rel="noopener noreferrer" {...rest}>{children}</a> : <Link to={to || "/"} {...rest}>{children}</Link>);

function Banner({ b }) {
  const t = THEMES[b.theme] || THEMES.green;
  const dark = b.theme !== "yellow";
  return (
    <div className={`relative min-h-[240px] overflow-hidden rounded-3xl ${t.bg}`} data-testid={`banner-${b.id}`}>
      <img src={b.image || t.fallback} alt={b.title} className="absolute inset-0 h-full w-full object-cover object-right" />
      <div className={`absolute inset-0 bg-gradient-to-r ${t.grad}`} />
      <div className="relative flex h-full max-w-[62%] flex-col justify-center gap-3 p-6 sm:p-8">
        {b.badge && <span className="w-fit rounded-full bg-bo-yellow px-2.5 py-0.5 text-[11px] font-bold uppercase text-bo-ink">{b.badge}</span>}
        <h2 className={`text-3xl font-extrabold sm:text-4xl ${dark ? "text-white" : "text-bo-ink"}`}>{b.title}</h2>
        {b.subtitle && <p className={`text-base font-medium ${dark ? "text-white/90" : "text-bo-ink/80"}`}>{b.subtitle}</p>}
        {b.cta_label && <SmartLink to={b.cta_link} className={`${t.cta} w-fit`} data-testid={LEGACY_CTA[b.id] || `banner-cta-${b.id}`}>{b.cta_label} <ArrowRight className="h-4 w-4" /></SmartLink>}
      </div>
    </div>
  );
}

const Head = ({ title, subtitle, link, linkLabel, testid, icon }) => (
  <div className="mb-3 flex items-end justify-between gap-3">
    <div><h2 className="flex items-center gap-2 text-lg font-bold">{icon}{title}</h2>{subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}</div>
    {link && <Link to={link} className="shrink-0 text-sm font-semibold text-bo-green hover:underline" data-testid={testid}>{linkLabel}</Link>}
  </div>
);

function Campaigns({ items, s }) {
  const today = new Date().toISOString().slice(0, 10);
  const live = items.filter((c) => c.active && (!c.starts_at || c.starts_at <= today) && (!c.ends_at || c.ends_at >= today));
  if (!live.length) return null;
  return (
    <section data-testid="home-campaigns">
      <Head title={s.title} subtitle={s.subtitle} icon={<Megaphone className="h-5 w-5 text-bo-green" />} />
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">{live.map((c) => (
        <SmartLink key={c.id} to={c.link} className="bo-card group flex overflow-hidden transition-transform hover:-translate-y-1" data-testid={`campaign-${c.id}`}>
          {c.image && <img src={c.image} alt="" className="h-28 w-28 shrink-0 object-cover" />}
          <div className="p-4"><p className="font-bold">{c.title}</p><p className="text-sm text-gray-600">{c.text}</p></div>
        </SmartLink>))}</div>
    </section>
  );
}

function CategoryStrip({ cats, s }) {
  return (
    <section data-testid="home-categories">
      <Head title={s.title} subtitle={s.subtitle} link="/catalogo" linkLabel="Ver tudo" testid="see-all-categories" />
      <div className="bo-scroll -mx-1 flex gap-3 overflow-x-auto px-1 pb-2">
        {cats.map((c) => (
          <Link key={c.key} to={`/catalogo?categoria=${c.key}`} data-testid={`category-card-${c.key}`} className="bo-card group w-[128px] shrink-0 overflow-hidden transition-transform hover:-translate-y-1">
            <div className="h-20 overflow-hidden bg-gray-100">{c.image && <img src={c.image} alt={c.name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" />}</div>
            <div className="p-2.5"><p className="truncate text-sm font-semibold">{c.name}</p><p className="text-[11px] text-gray-500">{c.count} produtos</p></div>
          </Link>
        ))}
      </div>
    </section>
  );
}

export default function Home() {
  const { site } = useSite();
  const [cats, setCats] = useState([]);
  const [deals, setDeals] = useState(null);
  const [popular, setPopular] = useState([]);
  const [featured, setFeatured] = useState([]);
  const featuredIds = (site?.featured_product_ids || []).join(",");
  useEffect(() => {
    api.get("/categories").then(({ data }) => setCats(data));
    api.get("/products", { params: { deals: true, sort: "discount", limit: 8 } }).then(({ data }) => setDeals(data));
    api.get("/products", { params: { limit: 8 } }).then(({ data }) => setPopular(data));
  }, []);
  useEffect(() => { if (featuredIds) api.get("/products", { params: { ids: featuredIds } }).then(({ data }) => setFeatured(data)); else setFeatured([]); }, [featuredIds]);
  if (!site) return <Spinner />;
  const render = (s) => {
    if (!s.active) return null;
    if (s.key === "banners") {
      const bs = site.banners.filter((b) => b.active);
      return bs.length ? <div key="banners" className="grid gap-4 md:grid-cols-2" data-testid="home-banners">{bs.map((b) => <Banner key={b.id} b={b} />)}</div> : null;
    }
    if (s.key === "campaigns") return <Campaigns key="campaigns" items={site.campaigns} s={s} />;
    if (s.key === "categories") return <CategoryStrip key="categories" cats={cats} s={s} />;
    if (s.key === "featured") return featured.length ? <section key="featured" data-testid="home-featured"><Head title={s.title} subtitle={s.subtitle} icon={<Sparkles className="h-5 w-5 text-bo-yellow" />} /><ProductGrid items={featured} /></section> : null;
    if (s.key === "deals") return <section key="deals" data-testid="home-deals"><Head title={s.title} subtitle={s.subtitle} link="/ofertas" linkLabel="Ver todas" testid="see-all-deals" icon={<Flame className="h-5 w-5 text-red-500" />} />{deals ? <ProductGrid items={deals} /> : <Spinner />}</section>;
    if (s.key === "compare") return <section key="compare" data-testid="home-compare"><Head title={s.title} subtitle={s.subtitle} /><ProductGrid items={popular} /></section>;
    return null;
  };
  return <div className="flex flex-col gap-7" data-testid="home-page">{site.home_sections.map(render)}</div>;
}
